import {useMemo} from 'react';
import {assert} from 'assert-ts';
import type {Schema} from 'schemaDefinition/types';
import type {CodeListMap, LocalCodeListId} from 'services/codelists/types';
import {useLocalization} from 'localization';
import {isLinkedRoleCodeListId} from 'schemaDefinition/functions';
import {AllLocalCodeListIds} from 'schemaDefinition/types/linkTypes';
import {selectCodelists} from 'services/codelists/codelistsSlice';
import {useAppSelector} from 'store/hooks';
import {getLinkedRoleCodelist} from 'schemas/links/functions';
import {useSchemaListCodelistIds} from './useSchemaListCodelistIds';

/**
 * Assumes needed codelists have been loaded
 */
export const useSchemasCodelists = (schemas: Schema[]): CodeListMap => {
  const localization = useLocalization();
  const codelistIds = useSchemaListCodelistIds(schemas);
  const codelists = useAppSelector(selectCodelists);

  return useMemo(() => {
    return codelistIds.reduce<CodeListMap>((acc, id) => {
      if (AllLocalCodeListIds.includes(id as LocalCodeListId)) {
        return acc;
      }

      if (isLinkedRoleCodeListId(id)) {
        acc[id] = getLinkedRoleCodelist(id, localization);
        return acc;
      }

      const codelist = assert(
        codelists[id],
        `useSchemaCodelists: missing codelist for id: ${id}`,
        {id},
      );
      assert(
        codelist.status === 'Loaded',
        'useSchemaCodelists: codelist not loaded',
        {id},
      );
      acc[id] = codelist.data;
      return acc;
    }, {} as CodeListMap);
  }, [codelistIds, codelists, localization]);
};
